import { useCallback, useEffect, useRef, useState } from 'react';
import { SessionLayout, useAutoHideVideo, useSessionUI } from '../../../ui';
import { usePostMessage, useReceiveMessage } from '../../../context/messageContext';
import { useCalibration } from '../../../context/calibrationContext';
import { useSessionState } from '../../../context/sessionStateContext';
import { useToolDetection } from '../../../helpers/distance-detection-hooks';
import { useCustomDeviceDimensions } from '../../../helpers/useCustomDeviceDimensions';
import { useMediaQuery } from '../../../helpers/useMediaQuery';
import { PatientFacialDetection } from '../../../components';
import { useSessionPeople, useConfirmExitSession } from '../hooks';
import { useCalibrationDevices } from './devices';
import { useCalibrationSync } from './sync';
import {
  AwaitMethodView,
  DeviceSelectView,
  CardInstructionsView,
  CardMeasureView,
  CardUnavailableView,
  RulerView,
  CameraToolView,
  DistanceView,
} from './PatientCalibrationViews';

// Patient calibration / distance screens (Figma P3-P6) inside the IBISengage SessionLayout (panel
// + Exit Session + video strip). Used by ResponsivePatientContainer (tablets / phones) and by
// patient-view.js (desktop). The ws messages and maths are the ones the legacy screens used
// (ResponsiveScreenCalibrationSelection, ResponsiveCardCalibration, ResponsiveRulerCalibration,
// ResponsiveCameraCalibrationPatientContainer, ResponsivePupillaryDistancePatient).
//
// Video: these screens never render their own <LocalVideo/>. The camera calibration hands the
// strip's local tile <video id={sid}> to the tool tracker (localTileProps.parentVideoRef), so there
// is a single local video element and no second camera stream.

const PatientCalibrationLayout = ({ children, localTileProps }) => {
  const people = useSessionPeople();
  const exitSession = useConfirmExitSession();
  return (
    <SessionLayout
      endAction={{ label: 'Exit Session', onClick: exitSession }}
      video={{
        localLabel: people.localLabel,
        localName: people.localName,
        remoteLabel: people.remoteLabel,
        remoteName: people.remoteName,
        order: 'remote-first',
        localTileProps,
      }}
    >
      {children}
    </SessionLayout>
  );
};

// ---------------------------------------------------------------------------------------------
// P3 / P3.1  (tab 'screen-calibration'): wait for the method, then Make / Device
// ---------------------------------------------------------------------------------------------
export const DeviceSelection = () => {
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { makes, models, loading } = useCalibrationDevices(make);
  const { confirm } = useSessionUI();
  const sendDeviceDimensions = useCustomDeviceDimensions();
  const { patchCalibration } = useCalibrationSync();

  const onMake = (value) => {
    setMake(value);
    setModel('');
    setConfirmed(false);
    setSubmitted(false);
  };

  const onModel = async (value) => {
    setModel(value);
    setConfirmed(false);
    setSubmitted(false);
    const ok = await confirm({ message: `You have selected ${value} as your device. Is this correct?`, yesLabel: 'Yes', noLabel: 'No' });
    if (!ok) {
      setModel('');
      return;
    }
    setConfirmed(true);
  };

  const onSubmit = () => {
    const device = models.find((m) => m.name === model);
    const sent = sendDeviceDimensions(make, model, device);
    if (!sent) return;
    setSubmitted(true);
    patchCalibration({ method: 'device', device: { make, model, screen_mm: [sent.width, sent.height] } });
  };

  return (
    <DeviceSelectView
      makes={makes}
      models={models}
      make={make}
      model={model}
      onMake={onMake}
      onModel={onModel}
      onSubmit={onSubmit}
      submitted={submitted}
      canSubmit={Boolean(make && model && confirmed && !submitted)}
      loading={loading}
    />
  );
};

export const PatientScreenCalibration = () => {
  const received = useReceiveMessage();
  const { state } = useSessionState();
  const [deviceRequested, setDeviceRequested] = useState(false);

  useEffect(() => {
    if (received?.type === 'stepDeviceCalibration') setDeviceRequested(true);
    if (received?.type === 'screen-calibration') setDeviceRequested(false);
  }, [received]);

  const device = deviceRequested || state?.sub_step === 'device';
  return <PatientCalibrationLayout>{device ? <DeviceSelection /> : <AwaitMethodView />}</PatientCalibrationLayout>;
};

// ---------------------------------------------------------------------------------------------
// P3.2  Card (tab 'card'); the video feed is hidden while it is on screen (Figma)
// ---------------------------------------------------------------------------------------------
const CardCalibrationContent = () => {
  const [stage, setStage] = useState('intro');
  const [cardWidth, setCardWidth] = useState(355);
  const [cardHeight, setCardHeight] = useState(222);
  const [submitted, setSubmitted] = useState(false);
  const smallScreen = useMediaQuery('(max-width: 700px)');
  const postMessage = usePostMessage();
  useAutoHideVideo(true);

  const sendCardDimensions = (width, height, initial) => {
    postMessage?.({
      type: 'cardDimensions',
      content: {
        cardWidthPx: width,
        cardHeightPx: height,
        screenWidthPx: window.screen.availWidth,
        screenHeightPx: window.screen.availHeight,
        initial: Boolean(initial),
      },
    });
  };

  // Same 1% steps as the legacy card screen; every change is mirrored to the clinician.
  const changeCardSize = (direction) => {
    setSubmitted(false);
    const factor = direction === 'decrease' ? -0.01 : 0.01;
    const width = Math.round(cardWidth + cardWidth * factor);
    const height = Math.round(cardHeight + cardHeight * factor);
    setCardWidth(width);
    setCardHeight(height);
    sendCardDimensions(width, height, true);
  };

  if (smallScreen) return <CardUnavailableView />;
  if (stage === 'intro') {
    return (
      <CardInstructionsView
        onBegin={() => {
          setStage('measure');
          sendCardDimensions(cardWidth, cardHeight, true);
        }}
      />
    );
  }
  return (
    <CardMeasureView
      width={cardWidth}
      height={cardHeight}
      submitted={submitted}
      onDecrease={() => changeCardSize('decrease')}
      onIncrease={() => changeCardSize('increase')}
      onSubmit={() => {
        setSubmitted(true);
        sendCardDimensions(cardWidth, cardHeight, false);
      }}
    />
  );
};

export const PatientCardCalibration = () => (
  <PatientCalibrationLayout>
    <CardCalibrationContent />
  </PatientCalibrationLayout>
);

// ---------------------------------------------------------------------------------------------
// Ruler (tab 'ruler')
// ---------------------------------------------------------------------------------------------
export const PatientRulerCalibration = ({ desktop = false }) => {
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const postMessage = usePostMessage();

  const submit = () => {
    postMessage?.({
      type: 'rulerDimensions',
      content: {
        rulerWidth: desktop ? parseInt(width, 10) : width,
        rulerHeight: desktop ? parseInt(height, 10) : height,
        // Desktop and responsive screens measured the screen differently; kept as they were.
        screenWidthPx: desktop ? window.screen.width : window.innerWidth,
        screenHeightPx: window.screen.height,
      },
    });
    setSubmitted(true);
  };

  return (
    <PatientCalibrationLayout>
      <RulerView
        widthMm={width}
        heightMm={height}
        onWidth={(v) => {
          setWidth(v);
          setSubmitted(false);
        }}
        onHeight={(v) => {
          setHeight(v);
          setSubmitted(false);
        }}
        onSubmit={submit}
        submitted={submitted}
        canSubmit={String(width).length >= 2 && String(height).length >= 2 && !submitted}
      />
    </PatientCalibrationLayout>
  );
};

// ---------------------------------------------------------------------------------------------
// Camera calibration with the IBIS tool (tab 'camera-calibration')
// ---------------------------------------------------------------------------------------------
export const PatientCameraCalibration = () => {
  const canvasDOM = useRef(null);
  const videoDOM = useRef(null);
  const [calibrating, setCalibrating] = useState(false);
  const received = useReceiveMessage();
  const postMessage = usePostMessage();
  const calibration = useCalibration();
  const { setShouldCalculatePerceivedFocalLength, calculatedPerceivedFocalLength, alertChange, setAlertChange } = useToolDetection({ videoDOM, canvasDOM });

  useEffect(() => {
    if (received?.type === 'initialiseToolDetections') {
      setCalibrating(true);
      setShouldCalculatePerceivedFocalLength(true);
    }
  }, [received, setShouldCalculatePerceivedFocalLength]);

  useEffect(() => {
    if (alertChange === true) {
      calibration?.updateCalibrationData({ perceivedFocalLength: calculatedPerceivedFocalLength });
      postMessage?.({ type: 'perceivedFocalLength', content: { perceivedFocalLength: calculatedPerceivedFocalLength } });
      setAlertChange(false);
      setCalibrating(false);
    }
  }, [calculatedPerceivedFocalLength, alertChange, calibration, postMessage, setAlertChange]);

  return (
    <PatientCalibrationLayout localTileProps={{ parentVideoRef: videoDOM }}>
      <canvas style={{ display: 'none' }} ref={canvasDOM} />
      <CameraToolView calibrating={calibrating} />
    </PatientCalibrationLayout>
  );
};

// ---------------------------------------------------------------------------------------------
// P5 / P6  Distance (tab 'pupillaryDistance')
// ---------------------------------------------------------------------------------------------
export const PatientDistance = () => {
  const [detectDistance, setDetectDistance] = useState(false);
  const [initialDistanceDetected, setInitialDistanceDetected] = useState(false);
  const [status, setStatus] = useState({});
  const received = useReceiveMessage();
  const calibration = useCalibration();
  const { state } = useSessionState();
  const distanceMm = calibration?.calibrationData?.distanceFromScreen || state?.distance?.mm;

  useEffect(() => {
    switch (received?.type) {
      case 'startPupillaryDistanceDetection':
        setStatus({});
        return setDetectDistance(true);
      case 'stopPupillaryDistanceDetection':
        return setDetectDistance(false);
      case 'patientMovementDetected':
        // Sent at the end of the detection (see PatientFacialDetection).
        setInitialDistanceDetected(true);
        return setDetectDistance(false);
      default:
        break;
    }
  }, [received]);

  const onStatus = useCallback((s) => setStatus(s || {}), []);
  const collected = !detectDistance && (initialDistanceDetected || Boolean(state?.distance?.positioned));
  const phase = detectDistance ? (status.moving && status.hasInitialDistance ? 'moving' : 'detecting') : collected ? 'collected' : 'waiting';

  return (
    <PatientCalibrationLayout>
      {detectDistance && <PatientFacialDetection withVideo={false} overlay={false} onStatus={onStatus} />}
      <DistanceView phase={phase} counter={status.counter} distanceMm={distanceMm} />
    </PatientCalibrationLayout>
  );
};
